import {OrganizationProduct} from './../_models/OrganizationProduct';
import {PaymentTerm} from './../_models/PaymentTerm';
import {Organization} from './../_models/Organization';
import {AlertifyService} from './alertify.service';
import { HttpClient } from '@angular/common/http';
import {Injectable} from '@angular/core';
import {lastValueFrom, Observable, Subject} from 'rxjs';
import {environment} from 'src/environments/environment';
import {AppAuthService} from './app-auth.service';
import {Router} from '@angular/router';
import {CreateOrganizationDto} from '../_dtos/organizations/create-organization-dto';
import {UpdateOrganizationDetailsDto} from '../_dtos/organizations/update-organization-details-dto';
import {EditOrganizationProductDto} from '../_dtos/organizations/edit-organization-product-dto';
import {CreateOrganizationProductDto} from '../_dtos/organizations/create-organization-product-dto';

@Injectable({
    providedIn: 'root'
})
export class OrganizationService {
    baseUrl = environment.apiUrl + 'organizations/';

    organizationsList: any[];

    suppliersSub = new Subject<Organization[]>();
    suppliers: Organization[];

    storesSub = new Subject<Organization[]>();
    stores: Organization[];

    producersSub = new Subject<Organization[]>();
    producers: Organization[];

    pseudoOrganizationsSub = new Subject<Organization[]>();
    pseudoOrganizations: Organization[];

    organizationSub = new Subject<Organization>();
    organization: Organization;

    organizationProductsSub = new Subject<OrganizationProduct[]>();
    organizationProducts: OrganizationProduct[];

    organizationProductSub = new Subject<OrganizationProduct>();
    organizationProduct: OrganizationProduct;

    organizationTypesSub = new Subject<string[]>();
    organizationTypes: string[];

    paymentTermsSub = new Subject<PaymentTerm[]>();
    paymentTerms: PaymentTerm[];

    constructor(private http: HttpClient, private afy: AlertifyService, private auth: AppAuthService, private router: Router) {
    }

    getSuppliers() {
        this.http.get(this.baseUrl + 'Suppliers')
            .subscribe((orgs: Organization[]) => {
                this.suppliers = orgs;
                this.suppliersSub.next(this.suppliers.slice());
                if (!this.suppliers.length) this.afy.message('No organizations to display');
            }, error => {
                this.afy.error(error.error);
            });
    }

    getOrganizations<T>() {
        return this.http.get<T[]>(this.baseUrl);
    }

    getProducers() {
        this.http.get(this.baseUrl + 'Producers')
            .subscribe((orgs: Organization[]) => {
                this.producers = orgs;
                this.producersSub.next(this.producers.slice());
                if (!orgs.length) this.afy.message('No organizations to display');
            }, error => this.afy.error(error.error));
    }

    getPesudoOrganizations() {
        this.http.get(this.baseUrl + 'Pseudo')
            .subscribe((orgs: Organization[]) => {
                this.pseudoOrganizations = orgs;
                this.pseudoOrganizationsSub.next(this.pseudoOrganizations.slice());
                if (!orgs.length) this.afy.message('No organizations to display');
            }, error => this.afy.error(error.error));
    }

    getOrganization(organizationId: number) {
        this.http.get(this.baseUrl + organizationId)
            .subscribe((org: Organization) => {
                this.organization = org;
                this.organizationSub.next(this.organization);
            }, error => {
                this.afy.error(error.error);
            });
    }

    createOrganization(dto: CreateOrganizationDto): Observable<any> {
        return this.http.post(this.baseUrl + 'createOrganization', dto);
    }

    updateOrganizationDetails(dto: UpdateOrganizationDetailsDto): Observable<Organization> {
        return this.http.post<Organization>(this.baseUrl + 'update', dto);
    }

    getOrganizationProducts(organizationId: number) {
        this.http.get(this.baseUrl + `GetProducts/${organizationId}`)
            .subscribe((organizationProducts: any[]) => {
                this.organizationProducts = organizationProducts;
                this.organizationProductsSub.next(this.organizationProducts);
                if (!organizationProducts.length) this.afy.message('No products to display');
            }, error => this.afy.error(error.error));
    }

    getOrganizationProductsWithConversions(organizationId: number) {
        this.http.get(this.baseUrl + `GetProducts/WithConversions/${organizationId}`)
            .subscribe((organizationProducts: any[]) => {
                this.organizationProducts = organizationProducts;
                this.organizationProductsSub.next(this.organizationProducts);
                if (!organizationProducts.length) this.afy.message('No products to display');
            }, error => this.afy.error(error.error));
    }

    getOrganizationProduct(opid: number) {
        this.http.get(this.baseUrl + `GetOrganizationProduct/${opid}`)
            .subscribe((op: OrganizationProduct) => {
                this.organizationProduct = op;
                this.organizationProductSub.next(this.organizationProduct);
            }, error => this.afy.error(error.error));
    }

    updateOrganizationProduct(dto: EditOrganizationProductDto): Observable<OrganizationProduct> {
        return this.http.post<OrganizationProduct>(this.baseUrl + 'updateProduct', dto);
    }

    createOrganizationProduct(dto: CreateOrganizationProductDto): Observable<any> {
        return this.http.post(this.baseUrl + 'addProduct', dto);
    }

    deleteProduct(organizationProductId): Observable<any> {
        return this.http.get(this.baseUrl + 'removeProduct/' + organizationProductId);
    }

    getOrganizationTypes() {
        this.http.get(this.baseUrl + 'GetOrganizationTypes/')
            .subscribe((types: string[]) => {
                this.organizationTypes = types;
                this.organizationTypesSub.next(this.organizationTypes);
                if (!this.organizationTypes.length) this.afy.message('No organization types.');
            }, error => this.afy.error(error.error));
    }

    getPaymentTerms(organizationId: number) {
        this.http.get(this.baseUrl + 'getPaymentTerms/' + organizationId)
            .subscribe((terms: PaymentTerm[]) => {
                this.paymentTerms = terms;
                this.paymentTermsSub.next(this.paymentTerms);
            }, e => this.afy.error(e.error));
    }

    getStores() {
        this.http.get(this.baseUrl + 'Stores/')
            .subscribe((stores: Organization[]) => {
                this.stores = stores;
                this.storesSub.next(this.stores);
            }, error => this.afy.error(error.error));
    }

    getOrgName(id: number) {
        return this.http.get<any>(this.baseUrl + 'Name/' + id);
    }

    getDataForAutocomplete<T>() {
        return this.http.get<T[]>(this.baseUrl + 'Autocomplete');
    }

    getStoresForAutocomplete<T>() {
        return this.http.get<T[]>(this.baseUrl + 'Autocomplete/stores');
    }

    deleteSupplier(selection: Organization) {
        return lastValueFrom(this.http.delete(this.baseUrl + `DeleteSupplier/${selection.id}`));
    }
}
